"use client";

import { useMemo } from "react";
import { Star } from "lucide-react";
import { Container } from "@/components/ui/container";
import { Section } from "@/components/ui/section";
import { cn } from "@/lib/utils";
import { buddifyReviews, buddyAIReviews } from "@/content/data/reviews";

type AppKey = "buddify" | "buddy-ai";

interface MixedReview {
  text: string;
  store: string;
  country: string;
  rating: number;
  app: AppKey;
}

function cyrb128(str: string) {
  let h1 = 1779033703;
  let h2 = 3144134277;
  let h3 = 1013904242;
  let h4 = 2773480762;
  for (let i = 0; i < str.length; i++) {
    const k = str.charCodeAt(i);
    h1 ^= k;
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
    h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 ^= k;
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
    h2 ^= Math.imul(h3 ^ (h3 >>> 13), 3266489909);
    h3 ^= k;
    h3 = Math.imul(h3 ^ (h3 >>> 16), 2246822507);
    h3 ^= Math.imul(h4 ^ (h4 >>> 13), 3266489909);
    h4 ^= k;
    h4 = Math.imul(h4 ^ (h4 >>> 16), 2246822507);
    h4 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  }
  return {
    h1: h1 >>> 0,
    h2: h2 >>> 0,
    h3: h3 >>> 0,
    h4: h4 >>> 0,
  };
}

function mulberry32(seed: number) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function deterministicShuffle<T>(array: T[], salt: string): T[] {
  const seed = cyrb128(salt).h1;
  const rand = mulberry32(seed);
  const copy = [...array];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function badgeStyle(variant: VariantKey, isAi: boolean) {
  if (isAi) return "bg-brand-violet-soft text-brand-violet border border-brand-violet-line";
  if (variant === "dark") return "bg-raised text-muted border border-line-strong";
  return "bg-brand-green-soft text-brand-green border border-brand-green-line";
}

type VariantKey = "light" | "dark" | "mixed";

function ReviewCard({ review, variant }: { review: MixedReview; variant: VariantKey }) {
  const isAi = review.app === "buddy-ai";

  return (
    <div
      className={cn(
        "flex-shrink-0 w-[280px] sm:w-80 p-5 rounded-xl border transition-transform duration-200 hover:scale-[1.02]",
        "bg-panel border-line"
      )}
    >
      <div className="flex items-center justify-between mb-3">
        <span
          className={cn(
            "text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full",
            badgeStyle(variant, isAi)
          )}
        >
          {isAi ? "Buddy AI" : "Brand Partnerships"}
        </span>

        <div className="flex items-center gap-1">
          {Array.from({ length: review.rating }).map((_, i) => (
            <Star
              key={i}
              className={cn(
                "w-3.5 h-3.5 fill-current",
                isAi ? "text-brand-violet" : "text-brand-green"
              )}
            />
          ))}
        </div>
      </div>

      <p
        className={cn(
          "text-sm leading-relaxed mb-4 line-clamp-4",
          "text-muted"
        )}
      >
        “{review.text}”
      </p>

      <div>
        <p
          className={cn(
            "text-xs font-semibold",
            "text-ink"
          )}
        >
          {review.store}
        </p>
        <p className={cn("text-[11px]", "text-muted")}>{review.country}</p>
      </div>
    </div>
  );
}

function MarqueeRow({
  reviews,
  direction,
  variant,
}: {
  reviews: MixedReview[];
  direction: "left" | "right";
  variant: VariantKey;
}) {
  const items = [...reviews, ...reviews, ...reviews, ...reviews];
  const fadeColor = "from-page";

  return (
    <div className="relative marquee-row">
      <div
        className={cn(
          "absolute left-0 top-0 bottom-0 w-12 sm:w-24 z-10 pointer-events-none",
          "bg-gradient-to-r",
          fadeColor,
          "to-transparent"
        )}
      />
      <div
        className={cn(
          "absolute right-0 top-0 bottom-0 w-12 sm:w-24 z-10 pointer-events-none",
          "bg-gradient-to-l",
          fadeColor,
          "to-transparent"
        )}
      />

      <div className="overflow-hidden">
        <div
          className="flex items-stretch gap-4 w-max marquee-track will-change-transform"
          style={{
            animation:
              direction === "left"
                ? "marquee 360s linear infinite"
                : "marquee-reverse 360s linear infinite",
          }}
        >
          {items.map((review, index) => (
            <ReviewCard
              key={`${review.store}-${review.app}-${index}`}
              review={review}
              variant={variant}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

interface ReviewsMarqueeProps {
  title?: string;
  subtitle?: string;
  variant?: VariantKey;
  reviews?: { text: string; store: string; country: string; rating: number }[];
  app?: AppKey;
  reviewHref?: string;
}

export function ReviewsMarquee({
  title = "Loved by Shopify merchants",
  subtitle = "Real reviews from real stores already growing with Buddify.",
  variant = "light",
  reviews,
  app = "buddify",
  reviewHref,
}: ReviewsMarqueeProps) {
  const allReviews: MixedReview[] = useMemo(() => {
    if (reviews) {
      return reviews.map((r) => ({ ...r, app }));
    }
    return [
      ...buddifyReviews.map((r) => ({ ...r, app: "buddify" as const })),
      ...buddyAIReviews.map((r) => ({ ...r, app: "buddy-ai" as const })),
    ];
  }, [reviews, app]);

  const row1 = useMemo(
    () => deterministicShuffle(allReviews, "row1-seed"),
    [allReviews]
  );
  const row2 = useMemo(
    () => deterministicShuffle(allReviews, "row2-seed"),
    [allReviews]
  );

  const sectionBg: "gray" | "dark" | "white" = variant === "dark" ? "dark" : "gray";

  return (
    <Section background={sectionBg} size="sm" className="overflow-hidden">
      <Container>
        <div className="text-center mb-10">
          <h2
            className={cn(
              "mb-3 text-display-1",
              variant === "dark" ? "text-ink" : "text-ink"
            )}
          >
            {title}
          </h2>
          {subtitle && (
            <p
              className={cn(
                "text-body-lg max-w-2xl mx-auto",
                variant === "dark" ? "text-muted" : "text-muted"
              )}
            >
              {subtitle}
              {reviewHref && (
                <>
                  {' '}
                  <a
                    href={reviewHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      "underline underline-offset-4 transition-colors",
                      app === "buddy-ai"
                        ? "text-brand-violet hover:text-brand-violet"
                        : "text-brand-green hover:text-brand-green"
                    )}
                  >
                    Read all reviews
                  </a>
                </>
              )}
            </p>
          )}
        </div>
      </Container>

      <div className="space-y-6">
        <MarqueeRow reviews={row1} direction="left" variant={variant} />
        <MarqueeRow reviews={row2} direction="right" variant={variant} />
      </div>
    </Section>
  );
}
